import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { SessionUser } from 'src/shared/session';

import type { FacebookLoginResultData } from 'src/app/shared/hooks/auth';
import { useRefreshSession } from 'src/app/shared/hooks/session';

type Result = ModelHookResult<
	EmptyObject,
	{
		handleSuccess: (data: FacebookLoginResultData) => Promise<void>;
		handleFailure: () => void;
	}
>;

export function useFacebookButton({
	onError,
	onSuccess,
}: {
	onError: (err: string) => void;
	onSuccess: (user: SessionUser) => void;
}): Result {
	const { translate } = useI18n();
	const refreshSession = useRefreshSession();
	const track = useTracking();

	return {
		data: {},
		actions: {
			handleFailure: useCallback(() => {
				onError(translate('corgi.login_or_signup.error.facebook.generic'));
			}, [onError, translate]),
			handleSuccess: useCallback(
				async (data) => {
					try {
						const newSession = await refreshSession();
						if (!newSession.user) {
							// FIXME even though this should not happen often, we could use a more specific message
							throw new Error(translate('corgi.login_or_signup.error.facebook.generic'));
						}
						void track(
							'successful_login_or_join',
							{
								context: 'log_in',
								id: `user_${newSession.user.id}`,
								login_or_join_method: 'manual_fb',
								login_type: data.existingUser ? 'facebook_login' : 'facebook_signup',
								// eslint-disable-next-line @typescript-eslint/naming-convention
								'new_user_this_session?': !data.existingUser,
								user_flow: 'payment',
							},
							{
								defaultOverrides: {
									user_id: parseInt(newSession.user.id, 10),
								},
							},
						);
						onSuccess(newSession.user);
					} catch (e) {
						onError((e as Error).message);
					}
				},
				[onError, onSuccess, refreshSession, track, translate],
			),
		},
	};
}
